import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11 } from "styled-components";
import React from "react";
import useSWR, { type Fetcher } from "swr";
import { useForm, FormProvider } from "react-hook-form";
import { useRouter } from "next/router";
import { useFormState } from "~/features/ui/form/use-form-state";
import { Wrap } from "~/features/ui/wrap";
import { Grid } from "~/features/ui/grid";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { takeFirst } from "~/features/app/utils";
import { MarketoFormField } from "~/features/marketo/marketo-form-field";
import { Checkbox } from "~/features/ui/form/checkbox";
import { useLocale } from "~/features/i18n/use-locale";
import { FieldError } from "~/features/ui/form/field-error";
import { scrollElementIntoView } from "~/features/app/utils";
import { validateRecaptcha, preloadRecaptcha } from "~/features/re-captcha/utils";
import { Link } from "~/features/ui/link";
import { Button } from "~/features/ui/button";
import { MARKETO_FIELD_MAP } from "~/features/marketo/utils";
import { type Variant } from "~/features/marketo/marketo-form-field";
import { type MarketoFormDataType, type MarketoFormField as MarketoFormFieldType } from "~/features/marketo/types";
import ArrowRight from "~/public/icons/arrow-right.svg";
import Loading from "~/public/icons/loading.svg";
const translations = {
  submit: {
    en: "Submit",
    zh: "提交",
    ja: "送信",
    ko: "제출"
  },
  privacy: {
    en: "I agree to allow Ouster to store and process my personal data.",
    zh: "我同意Ouster存储和处理我的个人数据。",
    ja: "Ousterに個人データの保存と処理を許可します。",
    ko: "Ouster가 내 개인 데이터를 저장하고 처리하는 데 동의합니다."
  }
} as const;
const variants = {
  default: {
    general: {
      "paddingTop": "4rem",
      "paddingBottom": "4rem",
      "@media (min-width: 1024px)": {
        "paddingLeft": "12rem",
        "paddingRight": "12rem",
        "paddingTop": "8rem",
        "paddingBottom": "8rem"
      }
    },
    footer: {
      "gridColumn": "1 / -1",
      "@media (min-width: 1024px)": {
        "gridColumn": "span 6 / span 6",
        "gridColumnStart": "7"
      }
    },
    submit: {
      "display": "flex",
      "flexDirection": "column",
      "alignItems": "flex-start",
      "gap": "4rem"
    }
  },
  condensed: {
    general: {
      "paddingTop": "4rem",
      "paddingBottom": "4rem",
      "@media (min-width: 1024px)": {
        "paddingLeft": "4rem",
        "paddingRight": "4rem"
      }
    },
    footer: {
      "gridColumn": "1 / -1"
    },
    submit: {
      "display": "flex",
      "flexDirection": "column",
      "alignItems": "flex-start",
      "gap": "4rem",
      "@media (min-width: 1024px)": {
        "flexDirection": "row",
        "alignItems": "center",
        "justifyContent": "space-between"
      }
    }
  }
} as const;
const fieldsFetcher: Fetcher<{
  data: MarketoFormFieldType[];
}, string> = (url: string) => fetch(url).then(res => res.json());
const formDataFetcher: Fetcher<{
  data: MarketoFormDataType;
}, string> = (url: string) => fetch(url).then(res => res.json());
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type MarketoFormBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  disclaimer?: React.ReactNode;
  fields?: MarketoFormFieldType[];
  formData?: MarketoFormDataType | null;
  hasTopDivider?: boolean | null;
  layout?: string | null;
  marketoFormId?: string | null;
  mode?: string | null;
  privacyCheckboxLabel?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  showWatchWebinarButton?: boolean | null;
  successMessage?: string | null;
  successMessageImage?: React.ReactNode;
  successMessageText?: React.ReactNode;
  theme?: string | null;
};
export function MarketoFormBlockView({
  anchor,
  disclaimer,
  fields: providedFields,
  formData: providedFormData,
  hasTopDivider = true,
  layout: layoutProp,
  marketoFormId,
  mode = "light",
  privacyCheckboxLabel,
  sectionProps,
  showWatchWebinarButton = false,
  successMessage,
  successMessageImage,
  successMessageText,
  theme = "white"
}: MarketoFormBlockViewProps) {
  const router = useRouter();
  const layout = layoutProp as Variant || "default";
  const urlParams = new URLSearchParams(router.asPath.split("?")[1]);
  const pathWithoutParams = router.asPath.split("?")[0];
  const shouldFetch = providedFields === undefined && Boolean(marketoFormId);
  const {
    data: fieldsData
  } = useSWR(shouldFetch ? `/api/get-marketo-form-fields?formId=${marketoFormId}` : null, fieldsFetcher);
  const {
    data: fetchedFormData
  } = useSWR(shouldFetch ? `/api/get-marketo-form-data?formId=${marketoFormId}` : null, formDataFetcher);
  const fields = providedFields ?? fieldsData?.data ?? [];
  const formData = providedFields === undefined ? fetchedFormData?.data : providedFormData;
  const defaultValues = Array.from(urlParams.entries()).reduce((acc, [key, value]) => {
    if (MARKETO_FIELD_MAP.hasOwnProperty(key)) {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, string>);
  const methods = useForm({
    defaultValues
  });
  const {
    handleSubmit,
    register,
    formState: {
      errors
    }
  } = methods;
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const [status, setStatus] = useFormState();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const locale = useLocale();
  const onSubmit = handleSubmit(async data => {
    delete data.privacy;
    setStatus({
      state: "LOADING"
    });
    const isRecaptchaValid = await validateRecaptcha();
    if (!isRecaptchaValid) {
      setStatus({
        state: "ERROR",
        message: "There was an issue with reCAPTCHA, please try again later."
      });
      return;
    }
    try {
      const res = await fetch("/api/submit-marketo-form", {
        method: "POST",
        body: JSON.stringify({
          fields: data,
          formId: marketoFormId,
          url: window.location.href
        })
      });
      if (res.status !== 200) {
        throw new Error("Error");
      }
      setStatus({
        state: "SUCCESS"
      });
      router.push({
        pathname: router.pathname,
        query: {
          ...router.query,
          formSubmitted: marketoFormId
        }
      }, undefined, {
        shallow: true
      });
      if (containerRef.current) {
        scrollElementIntoView(containerRef, 80);
      }
    } catch (e: any) {
      console.error(e);
      setStatus({
        state: "ERROR",
        message: e.message
      });
    }
  });
  return <_StyledSection {...sectionProps} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.text, themeStyle.border, themeStyle.background]}>
      <Wrap mode={mode as Mode}>
        <_StyledDiv>
          <_StyledDiv2 ref={containerRef} $_css2={[variants[layout].general, themeStyle.border]}>
            {status.state === "SUCCESS" ? <_StyledDiv3>
                <_StyledDiv4>
                  <_StyledH>{successMessage}</_StyledH>
                  {successMessageText}
                </_StyledDiv4>
                {successMessageImage}
                {showWatchWebinarButton && <div>
                    <Button as={Link} href={`${process.env.NEXT_PUBLIC_URL}${pathWithoutParams}/watch`} icon={ArrowRight}>
                      Watch Webinar
                    </Button>
                  </div>}
              </_StyledDiv3> : <form data-marketo-form-id={marketoFormId} onFocusCapture={() => preloadRecaptcha()}>
                <FormProvider {...methods}>
                  <_StyledGrid>
                    {fields.map((field, idx) => <MarketoFormField key={idx} disabled={status.state === "LOADING"} {...field} variant={layout as Variant} />)}
                  </_StyledGrid>
                </FormProvider>
                <_StyledGrid2>
                  <_StyledDiv5 $_css3={[{
                "display": "flex",
                "flexDirection": "column"
              }, variants[layout as Variant].footer]}>
                    {disclaimer}
                    <_StyledDiv6 $_css4={variants[layout].submit}>
                      <Checkbox name="privacy" label={privacyCheckboxLabel || translations.privacy[locale]} register={register("privacy", {
                    required: {
                      value: true,
                      message: "Consent is required"
                    }
                  })} error={errors["privacy"]?.message as string} />
                      <_StyledDiv7>
                        <Button onClick={onSubmit} variant="secondary" disabled={status.state === "LOADING"} icon={status.state === "LOADING" ? Loading : ArrowRight}>
                          {formData?.buttonLabel || translations.submit[locale]}
                        </Button>
                        {status.state === "ERROR" && status.message && <FieldError message={status.message} />}
                      </_StyledDiv7>
                    </_StyledDiv6>
                  </_StyledDiv5>
                </_StyledGrid2>
              </form>}
          </_StyledDiv2>
        </_StyledDiv>
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-1qcvg2p-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-1qcvg2p-1"
})({
  "position": "relative",
  "zIndex": "10",
  "paddingLeft": "4rem",
  "paddingRight": "4rem",
  "@media (min-width: 1024px)": {
    "paddingLeft": "0",
    "paddingRight": "0"
  }
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-1qcvg2p-2"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-1qcvg2p-3"
})({
  "display": "flex",
  "flexDirection": "column",
  "rowGap": "4rem"
});
var _StyledDiv4 = _styled5("div").withConfig({
  componentId: "sc-sf-1qcvg2p-4"
})({
  "display": "flex",
  "flexDirection": "column",
  "rowGap": "2.4rem"
});
var _StyledH = _styled6("h2").withConfig({
  componentId: "sc-sf-1qcvg2p-5"
})({
  "fontSize": "2.2rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.2rem",
    "lineHeight": "1.2",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
});
var _StyledGrid = _styled7(Grid).withConfig({
  componentId: "sc-sf-1qcvg2p-6"
})({
  "position": "relative",
  "zIndex": "10",
  "gap": "1.6rem"
});
var _StyledGrid2 = _styled8(Grid).withConfig({
  componentId: "sc-sf-1qcvg2p-7"
})({
  "marginTop": "1.6rem",
  "gap": "1.6rem",
  "@media (min-width: 1024px)": {
    "marginTop": "1.6rem"
  }
});
var _StyledDiv5 = _styled9("div").withConfig({
  componentId: "sc-sf-1qcvg2p-8"
})`${p => p.$_css3}`;
var _StyledDiv6 = _styled10("div").withConfig({
  componentId: "sc-sf-1qcvg2p-9"
})`${p => p.$_css4}`;
var _StyledDiv7 = /*#__PURE__*/_styled11("div").withConfig({
  displayName: "marketo-form___StyledDiv7",
  componentId: "sc-19vtxd4-0"
})({
  "position": "relative"
});